/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback, useState } from "react";
import { ITableColumn, ITableState, TABLE_SORT } from "./TableTypes";
import { useDeepCompareMemo } from "use-deep-compare";
import { ITableProps } from "./Table";

export interface IUseTableOptions {
	columns: ITableColumn[];
	defaultState?: Partial<ITableState>;
	preventMultisort?: boolean;
	multiSelect?: boolean;
}

export function useTable({
	columns,
	defaultState,
	multiSelect,
	preventMultisort
}: IUseTableOptions): [ITableProps] {
	const [ tableState, setTableState ] = useState<ITableState>({
		...defaultState,
		sort: [ ...(defaultState?.sort || []) ]
	});

	const columnsConf = useDeepCompareMemo(() => columns, [ columns ]);

	const onSortChange = useCallback(
		(columnId: string) => {
			setTableState((prevState) => {
				const sort = preventMultisort
					? prevState.sort.filter((sort) => sort.columnId === columnId)
					: [ ...prevState.sort ];
				const newState = { ...prevState, sort: sort };
				const sortConfIndex = newState.sort.findIndex((sortConf) => sortConf.columnId === columnId);

				if (sortConfIndex >= 0) {
					const prevConf = newState.sort[sortConfIndex];

					if (prevConf.direction === TABLE_SORT.ASC) {
						newState.sort[sortConfIndex] = {
							...newState.sort[sortConfIndex],
							direction: TABLE_SORT.DESC
						};

						return newState;
					}

					if (prevConf.direction === TABLE_SORT.DESC) {
						newState.sort.splice(sortConfIndex, 1);
						return newState;
					}
				}

				newState.sort.push({
					columnId,
					direction: TABLE_SORT.ASC
				});

				return newState;
			});
		},
		[ setTableState, preventMultisort ]
	);

	const onRowClick = useCallback(
		(rowId: string) => {
			setTableState((prevState) => {
				if (multiSelect) {
					const selectionIds = [ ...prevState.selectionIds ];
					const idIndex = selectionIds.findIndex((id) => id === rowId);

					if (idIndex > -1) {
						selectionIds.splice(idIndex, 1);
					} else {
						selectionIds.push(rowId);
					}

					return {
						...prevState,
						selectionIds
					};
				}

				return {
					...prevState,
					selectionIds: [ rowId ]
				};
			});
		},
		[ multiSelect ]
	);

	const tableProps: ITableProps = {
		columns: columnsConf,
		state: tableState,
		onSortChange: onSortChange,
		onRowClick: onRowClick
	};

	return [ tableProps ];
}
